import clsx from 'clsx'
import { AlertErrorCircle, Document, DraftSparkle, Template, XClear } from '@/assets/icons'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import TruncatedText from '@/components/truncated-text/TruncatedText'

export type FileChipVariant = 'file' | 'template' | 'draft'
export type FileChipSize = 'default' | 'large'
export type FileChipStatus = 'processing' | 'error'

export type FileChipProps = {
  /* Name shown on the chip. The full name shows in a hover label when it is truncated. */
  name: string
  variant?: FileChipVariant
  size?: FileChipSize
  /* Replaces the icon with a spinner, or with an error icon on a red chip */
  status?: FileChipStatus
  /* Added to the hover label in the error status */
  errorMessage?: string
  /* Makes the chip a button that calls this on click */
  onClick?: () => void
  /* Inactive state: gray content with no click or hover */
  disabled?: boolean
  /* Shows a remove button */
  onDismiss?: () => void
  /* Force the hover label open. Storybook/testing only. */
  forceTooltipOpen?: boolean
}

const kVariantIcons = {
  file: Document,
  template: Template,
  draft: DraftSparkle,
} as const

/**
 * A compact chip naming one file, template, or draft, optionally opening it on click and removing it with a dismiss
 * button. Presentational only: the caller resolves the name and status.
 */
export function FileChip({
  name,
  variant = 'file',
  size = 'default',
  status,
  errorMessage,
  onClick,
  disabled = false,
  onDismiss,
  forceTooltipOpen = false,
}: FileChipProps) {
  const isLarge = size === 'large'
  const isTemplate = variant === 'template'
  const isError = status === 'error' && !disabled
  const isClickable = !!onClick && !disabled
  const Icon = kVariantIcons[variant]
  const openLabel = variant === 'draft' ? `Open draft: ${name}` : `Open ${name}`

  // Content takes the hover colour from the chip, so it darkens wherever the pointer is on it
  const contentColor = disabled
    ? 'text-true-gray-500'
    : isError
    ? 'text-red-950'
    : isTemplate
    ? 'text-text-primary'
    : clsx('text-text-secondary', isClickable && 'hover-capable:group-hover:text-text-primary')
  const iconSize = isLarge ? 'size-5' : 'size-[18px]'

  const icon =
    status === 'processing' && !disabled ? (
      <span className={clsx('flex shrink-0 items-center justify-center', iconSize)}>
        <CircularProgressContinuousSized size={12} thickness={7} />
      </span>
    ) : isError ? (
      <span className={clsx('flex shrink-0 items-center justify-center', iconSize)}>
        <AlertErrorCircle className="size-4 text-red-700" />
      </span>
    ) : (
      <Icon className={clsx('shrink-0', iconSize, disabled ? 'text-true-gray-400' : contentColor)} />
    )

  // The padding sits on the parts rather than the chip, so the open button fills the chip up to the remove button
  const verticalPadding = isLarge ? 'py-1.5' : 'py-1'
  const contentClassName = clsx('flex min-w-0 items-center gap-1.5 self-stretch pl-2', verticalPadding, onDismiss ? 'pr-1' : 'pr-2')

  const content = (
    <>
      {icon}
      <TruncatedText
        clamp
        offsetX={-5}
        title={isError && errorMessage ? `${name} (${errorMessage})` : name}
        forceOpen={forceTooltipOpen}
        // On touch, clickable and disabled chips just show the truncated name. On a clickable chip a tap on the tooltip
        // would otherwise swallow the one opening it.
        disableOnTouch={isClickable || disabled}
        // break-all fills the line before clipping.
        className={clsx('break-all font-medium', isLarge ? 'text-sm leading-5 tracking-[-0.15px]' : 'text-xs leading-4', contentColor)}
      >
        {name}
      </TruncatedText>
    </>
  )

  return (
    <div
      data-ds-component="FileChip"
      className={clsx(
        // An inset ring draws the outline without adding to the chip's size, so every variant is the same height
        'group relative flex w-fit min-w-0 max-w-full cursor-default items-center rounded',
        // The chip shows the open button's focus, so the focus ring wraps the whole chip
        'has-[[data-chip-open]:focus-visible]:outline has-[[data-chip-open]:focus-visible]:outline-2 has-[[data-chip-open]:focus-visible]:outline-brand-500',
        disabled
          ? 'bg-true-gray-50 ring-1 ring-inset ring-true-gray-300'
          : isError
          ? clsx('bg-red-100 ring-1 ring-inset ring-red-200', isClickable && 'hover-capable:hover:bg-red-200')
          : isTemplate
          ? clsx('bg-brand-neutral-300', isClickable && 'hover-capable:hover:bg-brand-neutral-400')
          : variant === 'draft'
          ? clsx('bg-surface-content ring-1 ring-inset ring-border-strong', isClickable && 'hover-capable:hover:bg-surface-hover-strong')
          : clsx('bg-surface-content ring-1 ring-inset ring-border-default', isClickable && 'hover-capable:hover:bg-surface-hover')
      )}
    >
      {isClickable ? (
        <button
          type="button"
          onClick={onClick}
          aria-label={isError && errorMessage ? `${openLabel} (${errorMessage})` : openLabel}
          data-chip-open
          className={clsx(contentClassName, 'text-left focus:outline-hidden')}
        >
          {content}
        </button>
      ) : (
        <div className={contentClassName}>{content}</div>
      )}
      {/* data-chip-dismiss lets surrounding code find the remove button, for example to move focus to the next chip after a removal */}
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label={`Remove ${name}`}
          data-chip-dismiss
          className={clsx('group/dismiss flex shrink-0 items-center self-stretch pr-2 focus:outline-hidden', verticalPadding)}
        >
          <XClear
            className={clsx(
              'rounded group-focus-visible/dismiss:ring-2 group-focus-visible/dismiss:ring-brand-500',
              isLarge ? 'size-5' : 'size-[18px]',
              isError ? 'text-red-950' : 'text-text-tertiary'
            )}
          />
        </button>
      )}
    </div>
  )
}
